import React, { useState } from 'react';
import { ChevronDown, MapPin, Calendar, Plus, Eye, Trash2, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AllSportsHubsLogo from './ASH Logo (1).png';
import '../styles/SharedNavigation.css';

const SharedNavigation = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const resolvedUser = user
    ? {
        name: user.user_metadata?.full_name || user.email.split('@')[0],
        username: user.user_metadata?.username || user.email?.split('@')[0],
        email: user.email,
        avatar: null,
      }
    : {
        name: "Guest User",
        username: "guest",
        email: "guest@example.com",
        avatar: null,
      };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  // Helper function to check if current path matches
  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (paths) => paths.some(path => location.pathname.includes(path));

  return (
    <>
      <nav className="shared-navbar">
        <div className="shared-navbar-content">
          {/* Logo - Clickable and acts as home button */}
          <div 
            className="shared-logo-section" 
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={AllSportsHubsLogo} 
              alt="All Sports Hubs Logo" 
              className="shared-main-logo"
              style={{ height: '70px', width: 'auto', objectFit: 'contain' }}
            />
          </div>

          {/* Navigation Links - Home removed */}
          <ul className="shared-nav-links">
            {/* Grounds Dropdown */}
            <li className="shared-dropdown">
              <span 
                className={`shared-dropdown-trigger ${isDropdownActive(['grounds', 'ViewGrounds', 'CreateGround']) ? 'active' : ''}`}
                onClick={() => toggleDropdown('grounds')}
              >
                <MapPin size={18} />
                <span>Venues</span>
                <ChevronDown 
                  size={16} 
                  className={`shared-chevron ${activeDropdown === 'grounds' ? 'rotated' : ''}`}
                />
              </span>
              
              <ul className={`shared-dropdown-content ${activeDropdown === 'grounds' ? 'active' : ''}`}>
                <li>
                  <a 
                    href="/ViewGrounds"
                    className={`shared-dropdown-link ${isActive('/ViewGrounds') ? 'current' : ''}`}
                  >
                    <Eye size={16} />
                    <span>View Venues</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="/CreateGround"
                    className={`shared-dropdown-link ${isActive('/CreateGround') ? 'current' : ''}`}
                  >
                    <Plus size={16} />
                    <span>Create Venue</span>
                  </a>
                </li>
              </ul>
            </li>

            {/* Schedule Dropdown */}
            <li className="shared-dropdown">
              <span 
                className={`shared-dropdown-trigger ${isDropdownActive(['schedule']) ? 'active' : ''}`}
                onClick={() => toggleDropdown('schedule')}
              >
                <Calendar size={18} />
                <span>Schedule</span>
                <ChevronDown 
                  size={16} 
                  className={`shared-chevron ${activeDropdown === 'schedule' ? 'rotated' : ''}`}
                />
              </span>
              
              <ul className={`shared-dropdown-content ${activeDropdown === 'schedule' ? 'active' : ''}`}>
                <li>
                  <a 
                    href="/ViewSchedule"
                    className={`shared-dropdown-link ${isActive('/ViewSchedule') ? 'current' : ''}`}
                  >
                    <Eye size={16} />
                    <span>View Schedule</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="/CreateSchedule"
                    className={`shared-dropdown-link ${isActive('/CreateSchedule') ? 'current' : ''}`}
                  >
                    <Plus size={16} />
                    <span>Create Schedule</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="/DeleteSchedule" 
                    className={`shared-dropdown-link delete-item ${isActive('/DeleteSchedule') ? 'current' : ''}`}
                  >
                    <Trash2 size={16} />
                    <span>Delete Schedule</span>
                  </a>
                </li>
              </ul>
            </li>
          </ul>

          {/* User Profile Section */}
          <div className="shared-user-profile-section">
            <div 
              className="shared-user-profile-trigger"
              onClick={() => toggleDropdown('userProfile')}
            >
              <div className="shared-user-avatar">
                {resolvedUser.avatar ? (
                  <img src={resolvedUser.avatar} alt={resolvedUser.name} className="shared-avatar-image" />
                ) : (
                  <span className="shared-avatar-initials">{getInitials(resolvedUser.name)}</span>
                )}
              </div>
              <div className="shared-user-info">
                <span className="shared-user-name">{resolvedUser.name}</span>
                <span className="shared-user-username">@{resolvedUser.username}</span>
              </div>
              <ChevronDown 
                size={16} 
                className={`shared-user-chevron ${activeDropdown === 'userProfile' ? 'rotated' : ''}`}
              />
            </div>

            <div className={`shared-user-dropdown-content ${activeDropdown === 'userProfile' ? 'active' : ''}`}>
              <div className="shared-user-dropdown-header">
                <div className="shared-user-avatar-large">
                  {resolvedUser.avatar ? (
                    <img src={resolvedUser.avatar} alt={resolvedUser.name} className="shared-avatar-image" />
                  ) : (
                    <span className="shared-avatar-initials">{getInitials(resolvedUser.name)}</span>
                  )}
                </div>
                <div className="shared-user-details">
                  <h4>{resolvedUser.name}</h4>
                  <p>@{resolvedUser.username}</p>
                  <p className="shared-user-email">{resolvedUser.email}</p>
                </div>
              </div>
              
              <div className="shared-user-dropdown-divider"></div>
              
              <ul className="shared-user-dropdown-menu">
                <li>
                  <a href="/AccountSettings" className="shared-user-menu-item">
                    <Settings size={16} />
                    <span>Account Settings</span>
                  </a>
                </li>
                <li>
                  <button onClick={handleLogout} className="shared-user-menu-item shared-logout-btn">
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {/* Click outside overlay to close dropdowns */}
      {activeDropdown && (
        <div 
          className="shared-dropdown-overlay" 
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </>
  );
};

export default SharedNavigation;